import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getSkuBreakdown } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { SkuBreakdownItemModel } from '@/types/clarityLens'

type BarChartDataModel = {
  data: { name: string; value: number }[]
}

const DETAILED_INFORMATION_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'SKU Name',
    accessorKey: 'skuName',
    type: 'text',
  },
  {
    header: 'Status',
    accessorKey: 'status',
    type: 'text',
  },
  // {
  //   header: 'Min Code',
  //   accessorKey: 'minCode',
  //   type: 'text',
  // },
  {
    header: 'Manufacturer',
    accessorKey: 'manufacturer',
    type: 'text',
  },
  {
    header: 'Min Price Paid',
    accessorKey: 'minPricePaid',
    type: 'money',
  },
  {
    header: 'Max Price Paid',
    accessorKey: 'maxPricePaid',
    type: 'money',
  },
  {
    header: 'Avg Price Paid',
    accessorKey: 'avgPricePaid',
    type: 'money',
  },
  {
    header: 'Latest Price Paid',
    accessorKey: 'latestPricePaid',
    type: 'money',
  },
  {
    header: 'UOM',
    accessorKey: 'mfrUom',
    type: 'text',
  },
  {
    header: 'Avg Price/Unit',
    accessorKey: 'avgUnitPrice',
    type: 'money',
  },
  {
    header: 'Order Count',
    accessorKey: 'orderCount',
    type: 'number',
  },
  {
    header: 'First Order Date',
    accessorKey: 'firstOrderDate',
    type: 'text',
  },
  {
    header: 'Total Spend',
    accessorKey: 'totalSpend',
    type: 'money',
  },
  {
    header: '% of Spend',
    accessorKey: 'spendPercentage',
    type: 'percentage',
  },
]

export default function useSkuBreakdown(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [detailedInformationData, setDetailedInformationData] =
    useState<SkuBreakdownItemModel[]>()
  const [optimizationsChartData, setOptimizationsChartData] =
    useState<BarChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchSkuBreakdownData(
        branches,
        startDate,
        endDate,
        itemName
      )

      setDetailedInformationData(data)

      // Create bar chart data from SKU breakdown
      const barChart: BarChartDataModel = convertToBarChart(data)
      setOptimizationsChartData(barChart)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    detailedInformationData,
    detailedInformationColumns: DETAILED_INFORMATION_COLUMNS,
    optimizationsChartData,
    loading,
    refetch,
  }
}

async function fetchSkuBreakdownData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: SkuBreakdownItemModel[]
}> {
  try {
    const response = await getSkuBreakdown(
      branches,
      startDate,
      endDate,
      itemName
    )

    const data: SkuBreakdownItemModel[] = response.skus.map((sku) => ({
      skuName: sku.item_name,
      status: sku.approval_status,
      manufacturer: sku.manufacturer,
      minPricePaid: sku.min_price_paid,
      maxPricePaid: sku.max_price_paid,
      avgPricePaid: sku.avg_price_paid,
      latestPricePaid: sku.latest_price_paid,
      avgUnitPrice: sku.avg_unit_price,
      mfrUom: sku.mfr_uom,
      orderCount: sku.order_count,
      firstOrderDate: sku.first_order_date,
      totalSpend: sku.total_spend,
      spendPercentage: sku.spend_percentage,
    }))

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

// Convert SKU breakdown data to bar chart format
function convertToBarChart(
  skuData: SkuBreakdownItemModel[]
): BarChartDataModel {
  // Sort by average unit price and get top 10
  const sortedData = [...skuData]
    .sort((a, b) => b.avgUnitPrice - a.avgUnitPrice)
    .slice(0, 10)
    .map((sku) => ({
      name: sku.skuName,
      value: sku.avgUnitPrice,
    }))

  return {
    data: sortedData,
  }
}
